import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { useRole } from "@hooks/useRole";
import axios from "axios";
import cache from "@utils/cache";
import ActiveJobs     from "@features/dashboard/ActiveJobs";
import MyFavourites   from "@features/dashboard/MyFavourites";
import RecentSearches from "@features/dashboard/RecentSearches";
import MyNetwork          from "@features/dashboard/MyNetwork";
import AvailabilityDonut  from "@features/dashboard/AvailabilityDonut";
import JobFillRate        from "@features/dashboard/JobFillRate";
import ConnectIQUsageModal from "@features/dashboard/ConnectIQUsageModal";
import DashboardSectionCard from "@features/dashboard/DashboardSectionCard";
import { Modal, ModalFooter } from "@common/Modal";
import { API_BASE } from "@config/env";

const post = (ep, fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k,v]) => fd.append(k, v));
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};

const QUICK_ACTIONS = [
  { label:"Search Candidates", icon:"fa-magnifying-glass", color:"var(--fc-primary)", bg:"var(--fc-primary-light)", to:"/search" },
  { label:"Add Resource",      icon:"fa-user-plus",        color:"var(--fc-green)",   bg:"var(--fc-green-light)",   to:"/resources?add=1", hideForClient:true },
  { label:"Post a Job",        icon:"fa-briefcase",        color:"var(--fc-amber)",   bg:"var(--fc-amber-light)",   to:"/create-job" },
  { label:"Connect IQ AI",     icon:"fa-robot",            color:"var(--fc-purple)",  bg:"var(--fc-purple-light)",  to:"/connectiq", hideForClient:true },
];

function RemoveRecommendationModal({ recommendation, userId, onClose, onRemoved }) {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const res = await post("remove-recommendation-history-client", {
      u_id: userId,
      recommendation_id: recommendation.recommendation_id,
      remove_remark: remark,
    });
    setBusy(false);
    if (res?.status === 1) onRemoved();
  };

  return (
    <Modal title="Remove recommendation" onClose={onClose} maxWidth={440}
      footer={
        <ModalFooter onCancel={onClose} onConfirm={submit} confirmBusy={busy}
          confirmTone="danger" confirmDisabled={!remark.trim()}
          confirmLabel={busy ? "Removing..." : "Remove"} />
      }>
      <div style={{ fontSize:13, color:"var(--fc-text-secondary)", marginBottom:12 }}>
        {recommendation.resource_name} — {recommendation.user_profile_role || "No role set"}
      </div>
      <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:6, textTransform:"uppercase" }}>
        Reason for removal *
      </label>
      <textarea value={remark} onChange={e=>setRemark(e.target.value)} rows={4}
        placeholder="Let the recruiter know why you're removing this recommendation..."
        style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", resize:"vertical", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)" }} />
    </Modal>
  );
}

function RecommendedCandidates({ recommendations, userId, loading, onChanged, navigate }) {
  const [removeTarget, setRemoveTarget] = useState(null);

  return (
    <DashboardSectionCard title="Candidates recommended to you" subtitle="Sent to you by recruiters" icon="fa-user-check" colorKey="green">
      {loading ? (
        <div style={{ height:60, background:"var(--fc-bg-light)", borderRadius:9 }} />
      ) : recommendations.length === 0 ? (
        <div style={{ textAlign:"center", color:"var(--fc-text-subtle)", fontSize:13, padding:"20px 0" }}>No candidates recommended yet.</div>
      ) : recommendations.map((r, i) => (
        <div key={r.recommendation_id||i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:i<recommendations.length-1?"1px solid var(--fc-border-light)":"none" }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <i className="fa-solid fa-user" style={{ color:"#2563EB", fontSize:12 }} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:"var(--fc-text-primary)", cursor:"pointer" }} onClick={()=>navigate(`/profile/${r.user_slug}`)}>
              {r.resource_name}
            </div>
            <div style={{ fontSize:11, color:"var(--fc-text-subtle)" }}>{r.user_profile_role || "No role set"} — recommended by {r.resource_manager_name}</div>
          </div>
          <button onClick={()=>setRemoveTarget(r)}
            style={{ padding:"4px 10px", borderRadius:7, border:"1px solid var(--fc-border)", background:"var(--fc-card-bg)", color:"var(--fc-red)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>
            Remove
          </button>
        </div>
      ))}
      {removeTarget && (
        <RemoveRecommendationModal
          recommendation={removeTarget}
          userId={userId}
          onClose={()=>setRemoveTarget(null)}
          onRemoved={()=>{ setRemoveTarget(null); onChanged(); }}
        />
      )}
    </DashboardSectionCard>
  );
}

function StatCard({ label, value, icon, color, bg, sub, loading, onClick }) {
  return (
    <div onClick={onClick}
      style={{ borderRadius:16, padding:"16px 20px", background:bg, display:"flex", alignItems:"center", gap:14, cursor: onClick ? "pointer" : "default", transition:"opacity 0.15s" }}
      onMouseEnter={onClick ? e => e.currentTarget.style.opacity="0.85" : undefined}
      onMouseLeave={onClick ? e => e.currentTarget.style.opacity="1" : undefined}>
      <div style={{ width:44, height:44, borderRadius:12, background:"var(--fc-stat-icon-badge-bg)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize:18, color }} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:10, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4, opacity:0.75 }}>{label}</div>
        {loading
          ? <div style={{ height:22, width:60, background:"var(--fc-stat-skeleton-bg)", borderRadius:6 }} />
          : <div style={{ fontSize:22, fontWeight:900, color, lineHeight:1 }}>{value ?? "—"}</div>}
        {sub && !loading && <div style={{ fontSize:11, color, opacity:0.65, marginTop:3 }}>{sub}</div>}
      </div>
      {onClick && !loading && (
        <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize:11, color, opacity:0.5, flexShrink:0 }} />
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user, userSlug } = useAuth();
  const navigate = useNavigate();
  const { isRM, isClient, isResource, isFirmOwner } = useRole();

  // Resource role should never see this generic recruiter dashboard, no
  // matter how they arrive here (typed URL, bookmark, browser back button) -
  // the real post-login redirect is handled in LoginPage, this is the safety net.
  useEffect(() => {
    if (isResource && userSlug) navigate(`/profile/${userSlug}`, { replace: true });
  }, [isResource, userSlug]);

  const [counts,        setCounts]        = useState(null);
  const [favorites,     setFavorites]     = useState([]);
  const [searches,      setSearches]      = useState([]);
  const [network,       setNetwork]       = useState([]);
  const [usage,         setUsage]         = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showUsage,     setShowUsage]     = useState(false);

  const firmId    = user?.firm_details?.firm_id || user?.firm_id;
  const firstName = user?.first_name || user?.name?.split(" ")[0] || "there";
  const CACHE_KEY = `dashboard_${userSlug}`;

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  };

  const applyData = ([cnt, f, s, n, u, rec]) => {
    if (cnt) setCounts(cnt);
    setFavorites(Array.isArray(f?.result||f?.data) ? (f.result||f.data) : []);
    setSearches (Array.isArray(s?.result||s?.data) ? (s.result||s.data) : []);
    setNetwork  (Array.isArray(n?.result||n?.data) ? (n.result||n.data) : []);
    if (u?.status === 1) setUsage(u);
    setRecommendations(rec?.status === 1 ? rec.recommendations : []);
    setLoading(false);
  };

  const refresh = () => {
    if (!userSlug) return;
    cache.clear(CACHE_KEY); setLoading(true);
    Promise.all([
      post("get-dashboard-counts", { user_slug: userSlug }),
      post("favorite-profiles",    { user_slug: userSlug }),
      post("get-recent-searches",  { user_slug: userSlug }),
      post("get-firms-in-network", { user_slug: userSlug }),
      (isRM || isFirmOwner) ? post("connectiq/get_firm_usage", { user_slug: userSlug, range:"30" }) : Promise.resolve(null),
      isClient ? post("get-my-recommendation", { user_slug: userSlug }) : Promise.resolve(null),
    ]).then(res => { cache.set(CACHE_KEY, res); applyData(res); });
  };

  useEffect(() => {
    if (!userSlug) return;
    const cached = cache.get(CACHE_KEY);
    if (cached) { applyData(cached); return; }
    Promise.all([
      post("get-dashboard-counts", { user_slug: userSlug }),
      post("favorite-profiles",    { user_slug: userSlug }),
      post("get-recent-searches",  { user_slug: userSlug }),
      post("get-firms-in-network", { user_slug: userSlug }),
      (isRM || isFirmOwner) ? post("connectiq/get_firm_usage", { user_slug: userSlug, range:"30" }) : Promise.resolve(null),
      isClient ? post("get-my-recommendation", { user_slug: userSlug }) : Promise.resolve(null),
    ]).then(res => { cache.set(CACHE_KEY, res); applyData(res); });
  }, [userSlug]);

  const today = new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" });
  const result = counts?.result || counts;
  const credits = result?.firm_details?.parsing_credits ?? result?.credits;
  const expiry  = result?.firm_details?.expiry_date;
  const fmtDate = d => d ? new Date(d.replace(/-/g,"/")).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";
  const fmtTokens = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : n;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <h1 style={{ fontSize:21, fontWeight:800, color:"var(--fc-text-primary)", margin:0, letterSpacing:"-0.02em" }}>
            {getGreeting()}, {firstName} 👋
          </h1>
          <p style={{ fontSize:12, color:"var(--fc-text-subtle)", margin:"3px 0 0" }}>{today}</p>
        </div>
        <button onClick={refresh}
          style={{ display:"flex", alignItems:"center", gap:6, background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:9, padding:"7px 14px", fontSize:12, fontWeight:600, color:"var(--fc-text-muted)", cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor="var(--fc-primary)"; e.currentTarget.style.color="var(--fc-primary)"; }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor="var(--fc-card-border)"; e.currentTarget.style.color="var(--fc-text-muted)"; }}>
          <i className="fa-solid fa-rotate-right" style={{fontSize:11}} /> Refresh
        </button>
      </div>

      {/* ── Stat cards ── */}
      <div className="dash-stat-grid">
        <StatCard label="Total Resources" icon="fa-users"         color="var(--fc-green)" bg="var(--fc-green-light)" value={result?.resource_count} loading={loading} />
        {!isClient && (
          <>
            <StatCard label="Resume Parsing Credits" icon="fa-coins"         color="var(--fc-primary)" bg="var(--fc-primary-light)" value={credits} loading={loading} />
            <StatCard label="Renewal Date"    icon="fa-calendar-days" color="var(--fc-amber)"   bg="var(--fc-amber-light)"   value={fmtDate(expiry)} loading={loading} />
          </>
        )}
        {(isRM || isFirmOwner) && (
          <StatCard label="Connect IQ Usage" icon="fa-robot" color="var(--fc-purple)" bg="var(--fc-purple-light)"
            value={usage?.totals?.call_count ? fmtTokens(usage.totals.total_tokens)+" tokens" : "No usage"}
            loading={loading} onClick={() => setShowUsage(true)} />
        )}
      </div>

      {/* ── Quick actions ── */}
      <div className="dash-actions-grid">
        {QUICK_ACTIONS.filter(a => !(isClient && a.hideForClient)).map(a => (
          <button key={a.label} onClick={() => navigate(a.to)}
            style={{ display:"flex", alignItems:"center", gap:10, background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:12, padding:"11px 14px", cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s", textAlign:"left" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor=a.color+"50"; e.currentTarget.style.background=a.bg; e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 4px 12px ${a.color}20`; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor="var(--fc-card-border)"; e.currentTarget.style.background="var(--fc-card-bg)"; e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}>
            <div style={{ width:32, height:32, borderRadius:9, background:a.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <i className={`fa-solid ${a.icon}`} style={{ fontSize:14, color:a.color }} />
            </div>
            <span className="dash-action-label" style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-primary)", lineHeight:1.3 }}>{a.label}</span>
          </button>
        ))}
      </div>

      {/* ── Visual insights (not part of the client's simplified view) ── */}
      {!isClient && (
        <div className="dash-grid-equal">
          <AvailabilityDonut userSlug={userSlug} loading={loading} />
          <JobFillRate userSlug={userSlug} />
        </div>
      )}

      {/* ── Candidates recommended to you (client only) ── */}
      {isClient && (
        <RecommendedCandidates
          recommendations={recommendations}
          userId={user?.user_id}
          loading={loading}
          onChanged={refresh}
          navigate={navigate}
        />
      )}

      {/* ── Main sections ── */}
      <div className="dash-grid-equal">
        <MyFavourites   favorites={favorites} firmId={firmId}  loading={loading} />
        <RecentSearches searches={searches}                    loading={loading} />
      </div>

      <div className="dash-grid-wide">
        <ActiveJobs userSlug={userSlug} />
        <MyNetwork  network={network}   loading={loading} />
      </div>

      {showUsage && <ConnectIQUsageModal userSlug={userSlug} onClose={() => setShowUsage(false)} />}
    </div>
  );
}